import React from 'react';
import authService from '../../services/auth/service-auth';
import { useAppDispatch } from '../../ducks/hooks';
import loggerService from '../../services/logging/service-logger';
import { setLoggedUser } from '../../ducks/impersonation/duck-impersonation';
import { useTrackException, useTrackTrace } from '../../services/logging/hooks';
import { MSX_AUTH_NOT_FOUND } from '../../services/auth/service-auth-msx';
import { useFCPMarker } from '../../services/performance-observer/hooks';
import { LoadingPlaceholder } from '../../shared-components/hoc-loading-placeholder';

const MAX_AUTH_RETRIES = 3;
const INITIAL_BACKOFF_MS = 500;

export const AadProvider = (props: React.PropsWithChildren) => {
  const { children } = props;
  const dispatch = useAppDispatch();
  const trackException = useTrackException();
  const trackTrace = useTrackTrace();
  const [authUser, setAuthUser] = React.useState<string>('');

  useFCPMarker();
  React.useEffect(() => {
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const attemptAuthentication = async (retryCount: number = 0): Promise<string> => {
      try {
        const user = await authService.getLoggedAccountInfo();
        if (user) {
          return user;
        }
        throw new Error('User information not available');
      } catch (error: any) {
        // If it's MSX auth not found error, don't retry - handle fallback immediately
        if (error.message === MSX_AUTH_NOT_FOUND) {
          throw error;
        }

        // Log retry attempt information
        if (retryCount > 0) {
          trackTrace(`Authentication retry attempt ${retryCount} of ${MAX_AUTH_RETRIES}`, 'Seller Experience', 'AAD Provider', {
            error: error.message,
            retryCount,
            maxRetries: MAX_AUTH_RETRIES,
          });
        }

        trackException(error.message, 'Seller Experience', 'AAD Provider', { retryCount });

        // If max retries exceeded, throw the error
        if (retryCount >= MAX_AUTH_RETRIES) {
          throw error;
        }

        // Calculate exponential backoff: 500ms, 1000ms, 2000ms
        const backoffMs = INITIAL_BACKOFF_MS * Math.pow(2, retryCount);
        trackTrace(`Waiting ${backoffMs}ms before retry ${retryCount + 1}`, 'Seller Experience', 'AAD Provider', {
          backoffMs,
          nextRetry: retryCount + 1,
        });
        await sleep(backoffMs);

        // Retry the authentication
        return attemptAuthentication(retryCount + 1);
      }
    };

    const handleLogin = async () => {
      try {
        const user = await attemptAuthentication();
        loggerService.setUserContext(user);
        setAuthUser(user.split('@')[0]);
      } catch (error: any) {
        if (error.message === MSX_AUTH_NOT_FOUND && !authService.haveFallbacked) {
          trackTrace('MSX auth not found, falling back to MEM auth service', 'Seller Experience', 'AAD Provider', {
            haveFallbacked: authService.haveFallbacked,
          });
          authService
            .fallbackToMemAuthService()
            .then(async () => {
              trackTrace('Successfully fell back to MEM auth service, retrying authentication', 'Seller Experience', 'AAD Provider');
              await handleLogin();
            })
            .catch((e: any) => {
              trackException(e.message, 'Seller Experience', 'AAD Provider - MEM Fallback');
            });
          setAuthUser('');
        } else {
          trackException(error.message, 'Seller Experience', 'AAD Provider - Final Failure');
        }
      }
    };
    void handleLogin();
  }, [trackException, trackTrace]);

  React.useEffect(() => {
    if (!authUser) return;
    dispatch(setLoggedUser(authUser));
  }, [authUser, dispatch]);

  return (
    <LoadingPlaceholder isLoading={!authUser} includeStandaloneHeader>
      {<>{children as React.ReactElement<any>}</>}
    </LoadingPlaceholder>
  );
};
